import React, { useState } from "react";

// Formik validation
import * as Yup from "yup";
import { useFormik as useFormic } from "formik";

// Image
import logo from "assets/images/SOILOGO.png";
import { loginUser, socialLogin } from "slices/thunk";
import { useDispatch, useSelector } from "react-redux";
import withRouter, { WithRouterProps } from "Common/withRouter";
import { createSelector } from 'reselect';
import AuthIcon from "pages/AuthenticationInner/AuthIcon";
import { Link } from "react-router-dom";
import { WithTranslationProps } from "Common/Components/Translation/WithTranslationProps";
import withTranslation from "Common/withTranslation";
import { LoginViewModel } from "Common/ViewModels/Authentication/LoginViewModel";
import { LoginErrorsViewModel } from "Common/ViewModels/Errors/Authentication/LoginErrorViewModel";
import { axios_helper_async } from "helpers/Axios";
import withMessages, { WithMessagesProps } from "Common/withMessages";
import { MessageType } from "Common/Components/Messages/messageTypes";
import withNotifications, { WithNotificationsProps } from "Common/withNotifications";
import { NotificationType } from "Common/Components/Notifications/notificationTypes";
import { useAuth } from "Common/Components/Authentication/AuthenticationProvider";
import LoadingButton from "Common/Components/LoadingOverlay/LoadingButton/LoadingButton";

const modelFields: Record<keyof LoginViewModel, string> = {
    password: 'password',
    email: 'email'
}

const initialModel: LoginViewModel = {
    password: '',
    email: ''
}

const Login: React.FC<WithRouterProps & WithTranslationProps & WithMessagesProps & WithNotificationsProps> = ({router, i18n, t, messages, notifications}) => {
    const [model, setModel] = useState<LoginViewModel>(initialModel);
    const [errors, setErrors] = useState<LoginErrorsViewModel>({});
    const [remember, setRemember] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const { login } = useAuth();

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;

        setModel(prevModel => ({
            ...prevModel,
            [name]: value
        }));
    }

    const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { checked } = e.target;

        setRemember(checked);
    }

    const validModel = () => {
        let newErrors: LoginErrorsViewModel = {};

        if(!model.email) newErrors.email = "Login.Errors.Email";
        if(!model.password) newErrors.password = "Login.Errors.Password";

        setErrors(newErrors);

        return Object.keys(newErrors).length === 0;
    }

    const handelSignIn = async () => {
        if(validModel()) {
            try {
                setIsLoading(true);
                const response = await axios_helper_async<string>("/Account/Login", 'POST', model);

                if(response.isSuccess && response.data) {
                    login(response.data);
                    window.location.href = '/'
                }
                else {
                    messages.showMessage("Login.Errors.FailLoginMessage", MessageType.Error, "Login.Errors.FailLoginTitle");
                }
            }
            catch(err: any) {
                notifications.showNotification("AxiosAPI.FailedFetch", NotificationType.Error);
            }
            finally{
                setIsLoading(false);
            }
        }
    }

    return (
        <React.Fragment>
            <div className="relative">
                {/* <AuthIcon /> */}

                <div className="mb-0 w-screen lg:mx-auto lg:w-[500px] card shadow-lg border-none shadow-slate-100 relative">
                    <div className="!px-10 !py-12 card-body">
                        <Link to="/">
                            <img src={logo} alt="" className="h-10 mx-auto" />
                        </Link>

                        <div className="mt-8 text-center">
                            <h4 className="mb-1 text-custom-500 dark:text-custom-500">{t('Login.Welcome')}</h4>
                            <p className="text-slate-500 dark:text-zink-200">{t('Login.Subtitle')}</p>
                        </div>

                        <form className="mt-10" id="signInForm">
                            <div className="mb-3">
                                <label htmlFor="email" className="inline-block mb-2 text-base font-medium">{t('Login.Email')}</label>
                                <input
                                    type="email"
                                    id={modelFields.email}
                                    name={modelFields.email}
                                    className={`form-input ${errors.email ? "border border-red-500" : null}`}
                                    placeholder={t("Login.EmailPlaceholder")}
                                    onChange={handleChange}
                                    value={model.email}
                                />
                                {errors && errors.email ? (
                                    <div id="email-error" className="mt-1 text-sm text-red-500">{t(errors.email)}</div>
                                ) : null}

                            </div>
                            <div className="mb-3">
                                <label htmlFor="password" className="inline-block mb-2 text-base font-medium">{t('Login.Password')}</label>
                                <input
                                    type="password"
                                    id={modelFields.password}
                                    name={modelFields.password}
                                    className={`form-input ${errors.password ? "border border-red-500" : null}`}
                                    placeholder={t("Login.PasswordPlaceholder")}
                                    onChange={handleChange}
                                    value={model.password}
                                />
                                {errors && errors.password ? (
                                    <div id="email-error" className="mt-1 text-sm text-red-500">{t(errors.password)}</div>
                                ) : null}
                            </div>
                            {/* <div>
                                <div className="flex items-center gap-2">
                                    <input id="remember" checked={remember} onChange={handleCheckboxChange} className="w-4 h-4 border rounded-sm appearance-none bg-slate-100 border-slate-200 dark:bg-zink-600 dark:border-zink-500 checked:bg-custom-500 checked:border-custom-500 dark:checked:bg-custom-500 dark:checked:border-custom-500 checked:disabled:bg-custom-400 checked:disabled:border-custom-400" type="checkbox" />
                                    <label htmlFor="remember" className="inline-block text-base font-medium align-middle cursor-pointer">{t('Login.RememberMe')}</label>
                                </div>
                            </div> */}
                            {
                                isLoading ?
                                    <LoadingButton />
                                    :
                                <div className="mt-10">
                                    <button type="button" className="w-full btn btn-custom" onClick={handelSignIn}>{t('Login.BtnSignIn')}</button>
                                </div>
                            }
                            <div className="mt-10 text-center">
                                <p className="mb-0 text-slate-500 dark:text-zink-200">{t('Login.DontHaveAccount')} <Link to="/register" className="font-semibold underline transition-all duration-150 ease-linear text-slate-500 dark:text-zink-200 hover:text-custom-500 dark:hover:text-custom-500"> {t('Login.SignUp')}</Link> </p>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </React.Fragment>
    );
}

export default withRouter(withTranslation(withMessages(withNotifications(Login))));